// Neon character skins — unlockable rewards for progression.
// Free base skin available to everyone; others unlock based on personal
// high scores or Pro membership.

import { getHighScore } from "@/lib/highscore";
import { getIsPro, getIsSeason } from "@/lib/pro";
import { getStreak } from "@/lib/streak";
import { getBuggySurvivals, hasStormRider, shouldAwardStreakVault } from "@/lib/achievementsExtra";
import { hasFrenzySiren, hasVaultLadderBonus } from "@/lib/jackpotVault";

const STORAGE_KEY = "ppe_skin_v1";

export const SKINS = [
  {
    id: "cyan",
    name: "NEON CYAN",
    color: "#00F0FF",
    glow: "rgba(0,240,255,0.6)",
    unlock: () => true,
    hint: "DEFAULT",
  },
  {
    id: "magenta",
    name: "HOT MAGENTA",
    color: "#FF0055",
    glow: "rgba(255,0,85,0.6)",
    unlock: () => (getHighScore().outdoor || 0) >= 3000,
    hint: "REACH 3,000 · OUTDOOR",
  },
  {
    id: "lime",
    name: "TOXIC LIME",
    color: "#39FF14",
    glow: "rgba(57,255,20,0.6)",
    unlock: () => (getHighScore().underground || 0) >= 3000,
    hint: "REACH 3,000 · UNDERGROUND",
  },
  {
    id: "amber",
    name: "SUNSET AMBER",
    color: "#FF9020",
    glow: "rgba(255,144,32,0.65)",
    unlock: () => Math.max(getHighScore().outdoor || 0, getHighScore().underground || 0) >= 8000,
    hint: "REACH 8,000 · ANY LEVEL",
  },
  {
    id: "gold",
    name: "PRO GOLD",
    color: "#FFD400",
    glow: "rgba(255,212,0,0.65)",
    unlock: () => getIsPro(),
    hint: "PRO MEMBERSHIP",
  },
  // Streak reward skins
  {
    id: "streak7",
    name: "IRON STREAK",
    color: "#CD7F32",
    glow: "rgba(205,127,50,0.65)",
    unlock: () => getStreak().streak >= 7,
    hint: "7-DAY STREAK",
  },
  {
    id: "streak30",
    name: "DIAMOND STREAK",
    color: "#9EE7FF",
    glow: "rgba(158,231,255,0.7)",
    unlock: () => getStreak().streak >= 30,
    hint: "30-DAY STREAK",
  },
  {
    id: "streak100",
    name: "TITAN STREAK",
    color: "#C084FC",
    glow: "rgba(192,132,252,0.75)",
    unlock: () => getStreak().streak >= 100,
    hint: "100-DAY STREAK",
  },
  // Season Pass exclusive skin
  {
    id: "season_champion",
    name: "SEASON CHAMPION",
    color: "#F0ABFC",
    glow: "rgba(240,171,252,0.85)",
    unlock: () => getIsSeason(),
    hint: "SEASON PASS EXCLUSIVE",
  },
  // Season Track milestone skins — unlocked by claiming the milestone
  {
    id: "season_emerald",
    name: "EMERALD CHAMPION",
    color: "#39FF14",
    glow: "rgba(57,255,20,0.85)",
    unlock: () => {
      try {
        return getIsSeason() && (JSON.parse(localStorage.getItem("ppe_season_track_claimed_v1") || "[]").includes("emerald"));
      } catch { return false; }
    },
    hint: "SEASON TRACK · DAY 10",
  },
  {
    id: "season_ruby",
    name: "RUBY CHAMPION",
    color: "#FF3355",
    glow: "rgba(255,51,85,0.85)",
    unlock: () => {
      try {
        return getIsSeason() && (JSON.parse(localStorage.getItem("ppe_season_track_claimed_v1") || "[]").includes("ruby"));
      } catch { return false; }
    },
    hint: "SEASON TRACK · DAY 20",
  },
  {
    id: "season_diamond",
    name: "DIAMOND CHAMPION",
    color: "#B0F0FF",
    glow: "rgba(176,240,255,0.9)",
    unlock: () => {
      try {
        return getIsSeason() && (JSON.parse(localStorage.getItem("ppe_season_track_claimed_v1") || "[]").includes("diamond"));
      } catch { return false; }
    },
    hint: "SEASON TRACK · DAY 30",
  },
  // Rare drop skins (new)
  {
    id: "buggy_slayer",
    name: "BUGGY SLAYER",
    color: "#FF8A00",
    glow: "rgba(255,138,0,0.85)",
    unlock: () => getBuggySurvivals() >= 3,
    hint: "SURVIVE 3 BEACH BUGGIES",
  },
  {
    id: "storm_rider",
    name: "STORM RIDER",
    color: "#7DD3FC",
    glow: "rgba(125,211,252,0.85)",
    unlock: () => hasStormRider(),
    hint: "5 WINS UNDER ANY WEATHER",
  },
  {
    id: "vault_runner",
    name: "VAULT RUNNER",
    color: "#B0F0FF",
    glow: "rgba(176,240,255,0.9)",
    unlock: () => shouldAwardStreakVault(),
    hint: "WIN 5 ROULETTE RUNS IN A ROW",
  },
  {
    id: "frenzy_siren",
    name: "FRENZY SIREN",
    color: "#FF6EC7",
    glow: "rgba(255,110,199,0.9)",
    unlock: () => hasFrenzySiren(),
    hint: "CLAIM A JACKPOT VAULT DURING FRENZY WEEKEND",
  },
  {
    id: "ladder_boss",
    name: "LADDER BOSS",
    color: "#F0ABFC",
    glow: "rgba(240,171,252,0.9)",
    unlock: () => hasVaultLadderBonus(),
    hint: "RE-LOCK THE VAULT TWICE IN ONE FRENZY WEEKEND",
  },
];

export function getCurrentSkin() {
  try {
    const id = localStorage.getItem(STORAGE_KEY);
    const skin = SKINS.find((s) => s.id === id);
    if (skin && skin.unlock()) return skin;
  } catch {}
  return SKINS[0];
}

export function setCurrentSkinId(id) {
  try {
    const skin = SKINS.find((s) => s.id === id);
    if (skin && skin.unlock()) {
      localStorage.setItem(STORAGE_KEY, id);
      return skin;
    }
  } catch {}
  return getCurrentSkin();
}

// ---- Unlock detection ----
// Compares the currently-unlocked set to the previously-seen set stored in
// localStorage. Returns any newly-unlocked skins and updates the storage.
const SEEN_KEY = "ppe_skins_seen_v1";

export function detectNewlyUnlockedSkins() {
  let seen = [];
  try {
    seen = JSON.parse(localStorage.getItem(SEEN_KEY) || "[]");
  } catch {}
  const unlocked = SKINS.filter((s) => s.unlock()).map((s) => s.id);
  const newly = unlocked.filter((id) => !seen.includes(id));
  if (newly.length > 0) {
    try {
      localStorage.setItem(SEEN_KEY, JSON.stringify(unlocked));
    } catch {}
  }
  return newly.map((id) => SKINS.find((s) => s.id === id)).filter(Boolean);
}

// Call this once on first game load to prime the "seen" cache so previously
// unlocked skins don't fire the trailer.
export function primeSeenSkins() {
  try {
    if (!localStorage.getItem(SEEN_KEY)) {
      const unlocked = SKINS.filter((s) => s.unlock()).map((s) => s.id);
      localStorage.setItem(SEEN_KEY, JSON.stringify(unlocked));
    }
  } catch {}
}
